import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  Image,
  ImageBackground,
  ScrollView,
  StatusBar,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Platform,
  useWindowDimensions,
} from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import AmbientGlow from '../components/AmbientGlow';
import { Colors, Typography } from '../theme';
import { useProfile } from '../hooks/useProfile';
import { useDayVibes } from '../hooks/useDayVibes';
import type { RootStackParamList } from '../types';
import { useI18n } from '../i18n';
import { canAccessAuraVibe, canAccessVenueVibes } from '../utils/roles';
import { VibeCodeEntryLink } from '../features/vibeClaim/VibeCodeEntry';
import { useTheme } from '../theme/useTheme';
import { daylightStyle } from '../theme/daylightStyle';
import type { TonyteTheme } from '../theme/types';

interface Props {
  userId: string | null;
}

const BACKGROUND_IMAGE = require('../assets/BG.jpg');
// Card photos (optimised JPEGs, text is never baked into them).
const FIND_MY_VIBE_IMAGE = require('../assets/discover/vibes-find-my-vibe.jpg');
const VENUES_IMAGE = require('../assets/discover/vibes-venues.jpg');
const STUDIZONE_IMAGE = require('../assets/discover/vibes-studizone.jpg');
const BOTTOM_NAV_CLEARANCE = 106;

const hasRealProfilePhoto = (profile?: { photo?: string; profilePhotoURL?: string; profileImage?: string; photoPath?: string; profileImagePath?: string; profilePhotoPath?: string } | null) => {
  const photo = profile?.profilePhotoURL || profile?.profileImage || profile?.photo || '';
  const path = profile?.profileImagePath || profile?.photoPath || profile?.profilePhotoPath || '';
  return Boolean(
    path.trim() ||
    photo.includes('firebasestorage.googleapis.com') ||
    photo.includes('/profileImages/') ||
    photo.includes('/users/'),
  );
};

type DayVibesWindow = 'locked' | 'questions' | 'results';

function getDayVibesWindow(date = new Date()): DayVibesWindow {
  const hour = date.getHours();
  if (hour < 6) return 'locked';
  if (hour < 13) return 'questions';
  return 'results';
}

const DiscoverScreen: React.FC<Props> = ({ userId }) => {
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width >= 1024;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { t } = useI18n();
  const { theme } = useTheme();
  const appearance = useMemo(() => createDiscoverAppearanceStyles(theme), [theme]);
  const { profile } = useProfile(userId);
  const { state: dayVibesState, loading: dayVibesLoading } = useDayVibes(userId);
  const locked = profile && !hasRealProfilePhoto(profile);
  const [dayVibesWindow, setDayVibesWindow] = useState<DayVibesWindow>(() => getDayVibesWindow());
  const isVerifiedStudent = profile?.isStudentVerified === true && profile?.studentVerificationStatus === 'verified';

  useEffect(() => {
    const updateWindow = () => setDayVibesWindow(getDayVibesWindow());
    updateWindow();
    const id = setInterval(updateWindow, 60000);
    return () => clearInterval(id);
  }, []);

  const handleDayVibesTap = () => {
    if (locked) return;
    if (!canAccessAuraVibe(profile?.role)) return;
    if (dayVibesLoading) return;
    console.log('[Tonyte][DayVibes] day vibes tap', { dayVibesState });
    if (dayVibesState === 'submitted_waiting' || dayVibesState === 'submitted_unlocked') {
      console.log('[Tonyte][DayVibes] routing → DayVibesAura (already submitted)');
      navigation.navigate('DayVibesAura');
    } else {
      console.log('[Tonyte][DayVibes] routing → DayVibesQuestion (state:', dayVibesState, ')');
      navigation.navigate('DayVibesQuestion');
    }
  };

  // Discreet status on "Find my vibe" — the same windows and completion states
  // the quiz already uses (opens 6 AM, results 1 PM, live after that).
  const played = dayVibesState === 'submitted_waiting' || dayVibesState === 'submitted_unlocked';
  const quizStatus = useMemo<CardBadge | null>(() => {
    if (dayVibesWindow === 'locked') {
      return { icon: 'clock', text: t('discover.statusOpensAt').replace('{time}', `${t('discover.auraOpenTime')} ${t('discover.am')}`) };
    }
    if (dayVibesWindow === 'results') return { live: true, text: t('discover.statusResultsOut') };
    if (played) {
      return { icon: 'check', text: t('discover.statusResultsAt').replace('{time}', `${t('discover.auraResultsTime')} ${t('discover.pm')}`) };
    }
    return null;
  }, [dayVibesWindow, played, t]);

  // Responsive sizes: one wide card, two half-width cards under it.
  const pageWidth = isDesktopWeb ? Math.min(width, DESKTOP_MAX_WIDTH) : width;
  const contentWidth = pageWidth - PAGE_GUTTER * 2;
  const tileWidth = (contentWidth - CARD_GAP) / 2;
  const heroHeight = Math.max(188, Math.round(contentWidth / 1.9));
  const tileHeight = Math.max(196, Math.round(tileWidth / 0.86));
  const heroTitleSize = clamp(contentWidth * 0.115, 30, 46);
  const tileTitleSize = clamp(tileWidth * 0.155, 19, 30);

  return (
    <ImageBackground
      source={theme.isDark ? BACKGROUND_IMAGE : undefined}
      style={[styles.container, appearance.container]}
      resizeMode="cover"
    >
      <StatusBar barStyle={theme.statusBarStyle} translucent backgroundColor="transparent" />
      <LinearGradient
        colors={theme.isDark
          ? ['rgba(3,5,12,0.78)', 'rgba(4,8,18,0.9)', 'rgba(2,4,10,0.98)']
          : theme.gradients.background}
        locations={[0, 0.46, 1]}
        style={StyleSheet.absoluteFill}
      />
      <AmbientGlow tone="teal" size={260} intensity={theme.isDark ? 0.32 : 0.11} style={styles.topGlow} />
      <AmbientGlow tone="orange" size={300} intensity={theme.isDark ? 0.3 : 0.1} style={styles.bottomGlow} />
      <View style={[styles.particleA, appearance.particle]} />
      <View style={[styles.particleB, appearance.particle]} />
      <View style={[styles.particleC, appearance.particle]} />

      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.page,
          isDesktopWeb && styles.desktopPage,
          {
            paddingTop: isDesktopWeb ? 22 : insets.top + 18,
            paddingBottom: isDesktopWeb ? 22 : insets.bottom + BOTTOM_NAV_CLEARANCE,
          },
        ]}
      >
        <View style={styles.header}>
          <Text style={[styles.headerTitle, appearance.textPrimary, OUTFIT]} maxFontSizeMultiplier={1.3} accessibilityRole="header">
            {t('discover.heroTitle')}
          </Text>
          <Text style={[styles.headerSubtitle, appearance.textSecondary, OUTFIT]} maxFontSizeMultiplier={1.4}>
            {t('discover.vibesSubtitle')}
          </Text>
        </View>

        {locked ? (
          <View style={[styles.lockedPanel, appearance.lockedPanel]}>
            <Icon name="lock" size={16} color={Colors.sunset} />
            <Text style={[styles.lockedText, appearance.textSecondary]}>{t('discover.addPhotoLocked')}</Text>
          </View>
        ) : null}

        <VibeCard
          layout="hero"
          tint={TINTS.orange}
          image={FIND_MY_VIBE_IMAGE}
          title={t('discover.findMyVibeTitle')}
          description={t('discover.findMyVibeDesc')}
          badge={quizStatus}
          dimmed={Boolean(locked)}
          height={heroHeight}
          titleSize={heroTitleSize}
          shadowStyle={appearance.cardShadow}
          onPress={handleDayVibesTap}
          testID="vibes-card-quiz"
        />

        <View style={styles.tileRow}>
          <VibeCard
            layout="tile"
            tint={TINTS.violet}
            image={VENUES_IMAGE}
            title={t('venues.vibeAtVenues')}
            description={t('discover.venuesCardDesc')}
            badge={canAccessVenueVibes(profile?.role) ? null : { icon: 'tool', text: t('discover.underConstruction') }}
            height={tileHeight}
            titleSize={tileTitleSize}
            shadowStyle={appearance.cardShadow}
            onPress={() => navigation.navigate('VibeVenues')}
            testID="vibes-card-venues"
          />
          <VibeCard
            layout="tile"
            tint={TINTS.teal}
            image={STUDIZONE_IMAGE}
            title={t('discover.studiZoneTitle')}
            description={t('discover.studiZoneCardDesc')}
            // Everyone sees the card; StudiZone itself still shows its locked
            // state to anyone who isn't a verified student.
            badge={isVerifiedStudent ? null : { icon: 'lock', text: t('discover.studiZoneVerifiedOnly') }}
            height={tileHeight}
            titleSize={tileTitleSize}
            shadowStyle={appearance.cardShadow}
            onPress={() => navigation.navigate('StudiZone')}
            testID="vibes-card-studizone"
          />
        </View>

        {/* Played today's Vibe game on tonyte.app? Add those answers here. */}
        {Platform.OS !== 'web' && (
          <VibeCodeEntryLink
            signedIn
            variant="vibes"
            style={styles.webCodeLink}
            // One web code per quiz day: once today is played, the button
            // explains that instead of opening the code box.
            dayState={dayVibesLoading ? 'unknown' : played ? 'played' : dayVibesState === 'locked_until_open' ? 'not_open' : 'available'}
            onSeeVibes={() => navigation.navigate('DayVibesAura')}
          />
        )}
      </ScrollView>
    </ImageBackground>
  );
};

type CardBadge = { text: string; icon?: string; live?: boolean };
type Tint = { solid: string; fade: string; clear: string; shadow: string };

const TINTS: Record<'orange' | 'violet' | 'teal', Tint> = {
  orange: { solid: '#F27D26', fade: 'rgba(242,125,38,0.82)', clear: 'rgba(242,125,38,0)', shadow: 'rgba(242,125,38,0.45)' },
  violet: { solid: '#7A4FD8', fade: 'rgba(122,79,216,0.8)', clear: 'rgba(122,79,216,0)', shadow: 'rgba(122,79,216,0.45)' },
  teal: { solid: '#0F8583', fade: 'rgba(15,133,131,0.8)', clear: 'rgba(15,133,131,0)', shadow: 'rgba(15,133,131,0.45)' },
};

type VibeCardProps = {
  layout: 'hero' | 'tile';
  tint: Tint;
  image: number;
  title: string;
  description: string;
  badge?: CardBadge | null;
  dimmed?: boolean;
  height: number;
  titleSize: number;
  shadowStyle?: object;
  onPress: () => void;
  testID?: string;
};

// The whole card is one button; the round arrow is only a visual cue.
const VibeCard = memo(({ layout, tint, image, title, description, badge, dimmed, height, titleSize, shadowStyle, onPress, testID }: VibeCardProps) => {
  const hero = layout === 'hero';
  return (
    <TouchableOpacity
      activeOpacity={0.9}
      onPress={onPress}
      style={[styles.cardShell, hero ? styles.heroShell : styles.tileShell, { shadowColor: tint.shadow }, shadowStyle, dimmed && styles.cardDimmed]}
      accessibilityRole="button"
      accessibilityLabel={[title, description, badge?.text].filter(Boolean).join('. ')}
      accessibilityState={{ disabled: Boolean(dimmed) }}
      testID={testID}
    >
      <View style={[styles.cardClip, { minHeight: height, backgroundColor: tint.solid }]}>
        {/* Hero: the photo sits on the right so the text side stays solid;
            tiles: full-bleed photo, faces in the lower part. */}
        <Image source={image} style={hero ? styles.heroImage : styles.tileImage} resizeMode="cover" />
        <LinearGradient
          colors={[tint.solid, tint.solid, tint.fade, tint.clear]}
          locations={hero ? [0, 0.3, 0.42, 0.62] : [0, 0.3, 0.42, 0.66]}
          start={hero ? { x: 0, y: 0.5 } : { x: 0.5, y: 0 }}
          end={hero ? { x: 1, y: 0.5 } : { x: 0.5, y: 1 }}
          style={StyleSheet.absoluteFill}
        />
        <View style={[styles.cardText, hero ? styles.heroText : styles.tileText]}>
          <Text
            style={[styles.cardTitle, OUTFIT, { fontSize: titleSize, lineHeight: Math.round(titleSize * 1.06) }]}
            maxFontSizeMultiplier={1.25}
          >
            {title}
          </Text>
          <Text style={[styles.cardDescription, hero && styles.heroDescription, OUTFIT]} maxFontSizeMultiplier={1.35}>
            {/* One short sentence per line on the wide card ("Play the quiz. / Meet your people."). */}
            {hero ? description.replace(/([.!?])\s+/g, '$1\n') : description}
          </Text>
          {badge ? (
            <View style={styles.badge}>
              {badge.live ? <View style={styles.liveDot} /> : null}
              {badge.icon ? <Icon name={badge.icon} size={11} color="#FFFFFF" /> : null}
              <Text style={styles.badgeText} numberOfLines={1} maxFontSizeMultiplier={1.3}>{badge.text}</Text>
            </View>
          ) : null}
        </View>
        <View style={[styles.arrow, hero && styles.heroArrow]} pointerEvents="none">
          <Icon name="arrow-right" size={hero ? 22 : 20} color="#1C140E" />
        </View>
      </View>
    </TouchableOpacity>
  );
});
VibeCard.displayName = 'VibeCard';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const OUTFIT = Platform.select({ web: { fontFamily: 'Outfit, sans-serif' }, default: {} });
const PAGE_GUTTER = 18;
const CARD_GAP = 14;
const DESKTOP_MAX_WIDTH = 760;

const styles = StyleSheet.create({
  webCodeLink: {
    marginTop: 8,
  },
  container: {
    flex: 1,
    backgroundColor: '#03050C',
  },
  page: {
    flexGrow: 1,
    paddingHorizontal: PAGE_GUTTER,
    gap: CARD_GAP,
  },
  desktopPage: { width: '100%', maxWidth: DESKTOP_MAX_WIDTH, alignSelf: 'center' },
  topGlow: {
    top: -150,
    right: -120,
  },
  bottomGlow: {
    bottom: -170,
    left: -130,
  },
  particleA: {
    position: 'absolute',
    top: 150,
    right: 38,
    width: 3,
    height: 3,
    borderRadius: 2,
    backgroundColor: 'rgba(255,255,255,0.65)',
  },
  particleB: {
    position: 'absolute',
    top: 252,
    left: 30,
    width: 2,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'rgba(255,194,243,0.8)',
  },
  particleC: {
    position: 'absolute',
    top: 92,
    left: '52%',
    width: 2,
    height: 2,
    borderRadius: 1,
    backgroundColor: 'rgba(167,139,250,0.88)',
  },
  header: {
    marginBottom: 8,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 46,
    lineHeight: 52,
    fontWeight: Typography.extrabold,
    letterSpacing: -0.5,
  },
  headerSubtitle: {
    marginTop: 2,
    color: 'rgba(255,255,255,0.86)',
    fontSize: 16,
    fontWeight: Typography.medium,
    letterSpacing: 1.4,
  },
  lockedPanel: {
    minHeight: 36,
    borderRadius: 16,
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    backgroundColor: 'rgba(242,125,38,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(242,125,38,0.24)',
  },
  lockedText: {
    color: 'rgba(255,238,225,0.86)',
    fontSize: 12,
    lineHeight: 17,
    flex: 1,
  },
  tileRow: {
    flexDirection: 'row',
    gap: CARD_GAP,
  },
  cardShell: {
    borderRadius: 26,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.5,
    shadowRadius: 18,
    elevation: 8,
  },
  heroShell: { width: '100%' },
  tileShell: { flex: 1, minWidth: 0 },
  cardDimmed: { opacity: 0.6 },
  cardClip: {
    borderRadius: 26,
    overflow: 'hidden',
  },
  heroImage: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '26%',
    right: 0,
    width: undefined,
    height: undefined,
  },
  tileImage: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    width: undefined,
    height: undefined,
  },
  cardText: {
    gap: 6,
  },
  heroText: {
    width: '58%',
    paddingTop: 24,
    paddingBottom: 24,
    paddingLeft: 22,
    paddingRight: 8,
  },
  tileText: {
    paddingTop: 18,
    paddingHorizontal: 16,
    paddingBottom: 70,
  },
  cardTitle: {
    color: '#FFFFFF',
    fontWeight: Typography.extrabold,
    letterSpacing: -0.3,
  },
  cardDescription: {
    color: 'rgba(255,255,255,0.94)',
    fontSize: 14,
    lineHeight: 19,
    fontWeight: Typography.medium,
  },
  heroDescription: {
    fontSize: 16,
    lineHeight: 22,
  },
  badge: {
    alignSelf: 'flex-start',
    marginTop: 4,
    maxWidth: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 9,
    paddingVertical: 4,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.26)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  badgeText: {
    flexShrink: 1,
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: Typography.bold,
  },
  liveDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#4ADE80',
  },
  arrow: {
    position: 'absolute',
    right: 14,
    bottom: 14,
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF6EA',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
    elevation: 4,
  },
  heroArrow: {
    width: 52,
    height: 52,
    borderRadius: 26,
    right: 18,
    bottom: 18,
  },
});

const createDiscoverAppearanceStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, {
    backgroundColor: theme.colors.background,
  }),
  particle: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondaryBorder,
    opacity: 0.42,
  }),
  textPrimary: daylightStyle(theme, {
    color: theme.colors.textPrimary,
    textShadowColor: 'transparent',
  }),
  textSecondary: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  lockedPanel: daylightStyle(theme, {
    backgroundColor: 'rgba(242,125,38,0.09)',
    borderColor: 'rgba(196,92,16,0.22)',
  }),
  cardShadow: daylightStyle(theme, {
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 6,
  }),
});

export default DiscoverScreen;
